import { isNil, round, isString } from 'lodash';
import numberFormatUtils from '@/utils/number-format-utils';

const { formatStringToNumber, formatNumber, stringToNumberIfNumeric } = numberFormatUtils;

export function useFormatNumber() {
  /**
   * Rounds a number to a specified number of decimal places.
   * @param {Number | String} value The value to be rounded.
   * @param {Number} decimalPlaces The number of decimal places to round to.
   * @returns {Number | String} The rounded number (as a Number). Empty string is returned for null or undefined values.
   * When the provided value was a string that could not be converted to a number, the original string is returned.
   */
  const getNumberRoundedToDecimalPlaces = (value, decimalPlaces) => {
    // Remove null values
    if (isNil(value)) return '';

    // Try to convert string values to numbers. If the conversion fails, return the original string.
    const castedValue = isString(value) ? formatStringToNumber(value) : value;
    if (isString(castedValue)) return castedValue;

    return round(castedValue, decimalPlaces);
  };

  /**
   * Formats the given value as the decimal representation of a percentage, where the percentage itself
   * would be formatted to one decimal place. When isPercentage is true, the value will be formatted as a percentage
   * with one decimal - e.g. 0.15232323 -> 15.2%.
   * @param {Number | String} value The value to be formatted.
   * @param {Boolean} isPercentage Whether the value is a percentage and should be formatted as such.
   * @returns {String} The formatted value. Empty string is returned for null or undefined values.
   * When the provided value was a string that could not be converted to a number, the original string is returned.
   */
  const formatNumberWithOneDecimal = (value, isPercentage = false) => {
    const roundedNumber = getNumberRoundedToDecimalPlaces(value, 3);
    if (isString(roundedNumber)) return roundedNumber;

    const format = isPercentage ? 'percentagesWithOneDecimal' : 'numberWithOneDecimal';

    return formatNumber({ number: roundedNumber, format });
  };

  /**
   * Takes a number input and formats it to be a margin percent
   * @deprecated NOTE: This function is and has only been kept for backwards compatibility.
   * Please consider using formatNumberWithOneDecimal because margin, markdown, and waste
   * should all now be formatted to at least 1 decimal place. If you find no remaining references
   * to this function, please remove it.
   *
   * eg. 0.1462323 -> 0.15
   * @param {number} margin
   * @returns {string} Formatted Margin
   */
  const formatMargin = (margin, format = 'percentages') => {
    // Remove null values
    if (isNil(margin)) return '';
    margin = isString(margin) ? formatStringToNumber(margin) : margin;
    if (isString(margin)) return margin;

    // Round to 2 decimal places
    // ie. 0.15232323 -> 0.15
    margin = round(margin, 2);

    // Format into a percentage
    // ie. 0.15 -> 15%
    let formattedMargin = formatNumber({ number: margin, format });
    // To avoid displaying negative zero, we format it and compare against the value
    // keep the percentages format as default here since negative zero only happen in this format
    const negativeZero = formatNumber({ number: -0, format: 'percentages' });
    // If it is set we change it to be zero
    if (formattedMargin === negativeZero)
      formattedMargin = formatNumber({ number: 0, format: 'percentages' });
    return formattedMargin;
  };

  const formatTrendApplied = value => {
    if ([null, undefined, ''].includes(value)) {
      return value;
    }
    // Convert to string and remove any % symbols before adding one
    const cleanValue = String(value).replace(/%/g, '');
    return `${cleanValue}%`;
  };

  const getRoundedPercentage = (percentage, isMultiplied = false) => {
    if (!percentage) return null;
    const value = isMultiplied ? percentage : percentage * 100;
    const roundedPercentage = round(value);
    return stringToNumberIfNumeric(roundedPercentage);
  };

  return {
    formatStringToNumber,
    formatNumber,
    formatMargin,
    formatNumberWithOneDecimal,
    formatTrendApplied,
    getRoundedPercentage,
  };
}
